<template>
  <v-dialog v-model="dialogModel" max-width="900px" persistent>
    <v-card>
      <v-card-title class="d-flex align-center justify-space-between pb-2">
        <span class="headline text-h6 font-weight-bold">
          Gắn mã vận đơn: <span class="primary--text">{{ transactionItem ? transactionItem.TransactionCode : '' }}</span>
        </span>
        <v-btn icon @click="closeDialog">
          <v-icon>fa-times</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-2">
        <v-layout row wrap align-center class="mb-3">
          <v-flex xs12 sm6>
            <div class="subtitle-2 font-weight-bold mb-1">Chọn loại đơn:</div>
            <v-radio-group v-model="orderType" row class="mt-0 pt-0" @change="onOrderTypeChange">
              <v-radio label="Đơn mua hộ (MD)" :value="0" color="primary"></v-radio>
              <v-radio label="Đơn ký gửi (KG)" :value="1" color="success"></v-radio>
            </v-radio-group>
          </v-flex>

          <v-flex xs12 sm6>
            <v-text-field
              v-model="searchQuery"
              label="Tìm kiếm theo Mã đơn (ID), Mã HĐ, Mã KH..."
              clearable
              single-line
              outlined
              dense
              hide-details
              append-icon="fa-search"
              @click:append="fetchOrders"
              @keyup.enter="fetchOrders"
            ></v-text-field>
          </v-flex>
        </v-layout>

        <v-data-table
          :headers="headers"
          :items="items"
          :options.sync="pagination"
          :server-items-length="totalRows"
          :loading="loading"
          :footer-props="footerProps"
          class="elevation-1"
          dense
          :mobile-breakpoint="100"
        >
          <template v-slot:item.DisplayId="{ item }">
            <span class="font-weight-bold primary--text">
              {{ (orderType === 1 ? 'KG' : 'MD') + item.ID }}
            </span>
          </template>

          <template v-slot:item.OrderCodeCN="{ item }">
            <span>{{ item.OrderCodeCN || '—' }}</span>
          </template>

          <template v-slot:item.CustomerInfo="{ item }">
            <span>{{ item.UserCode || item.CustomerName || item.Username || '—' }}</span>
          </template>

          <template v-slot:item.CreatedDate="{ item }">
            <span>{{ item.CreatedDate | display_date }}</span>
          </template>

          <template v-slot:item.Status="{ item }">
            <span v-if="orderType === 0">{{ item.Status | order_status }}</span>
            <span v-else :style="{ color: getStatusColor(item.Status) }">
              {{ item.Status | display_value(packageStatus) }}
            </span>
          </template>

          <template v-slot:item.Action="{ item }">
            <v-btn
              color="primary"
              x-small
              :loading="submitting && selectedOrderId === item.ID"
              :disabled="submitting"
              @click="attachOrder(item)"
            >
              Gắn đơn
            </v-btn>
          </template>

          <template v-slot:no-data>
            <div class="text-center py-4 gray--text">Không tìm thấy đơn hàng nào.</div>
          </template>
        </v-data-table>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="grey darken-1" text @click="closeDialog">Đóng</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Vue from 'vue';
import configs from '@/configs';
import { request_list, update_detail } from '@/commons/hpod';
import { sendSuccessNotice, sendErrorNotice } from '@/commons/utils';

export default {
  name: 'AttachOrderPopup',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    transactionItem: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      orderType: 0, // 0: Mua hộ, 1: Ký gửi
      searchQuery: '',
      items: [],
      totalRows: 0,
      loading: false,
      submitting: false,
      selectedOrderId: null,
      pagination: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['ID'],
        sortDesc: [true]
      },
      footerProps: {
        itemsPerPageOptions: [10, 20, 50]
      },
      packageStatus: configs.PACKAGE_STATUS
    };
  },
  computed: {
    dialogModel: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },
    headers() {
      if (this.orderType === 0) {
        return [
          { text: 'Mã đơn', value: 'DisplayId', sortable: true },
          { text: 'Mã đơn TQ', value: 'OrderCodeCN', sortable: false },
          { text: 'Khách hàng', value: 'CustomerInfo', sortable: false },
          { text: 'Ngày tạo', value: 'CreatedDate', sortable: true },
          { text: 'Trạng thái', value: 'Status', sortable: false },
          { text: 'Thao tác', value: 'Action', sortable: false, align: 'center' }
        ];
      }
      return [
        { text: 'Mã đơn', value: 'DisplayId', sortable: true },
        { text: 'Khách hàng', value: 'CustomerInfo', sortable: false },
        { text: 'Ngày tạo', value: 'CreatedDate', sortable: true },
        { text: 'Trạng thái', value: 'Status', sortable: false },
        { text: 'Thao tác', value: 'Action', sortable: false, align: 'center' }
      ];
    }
  },
  watch: {
    value(val) {
      if (val) {
        this.pagination.page = 1;
        this.searchQuery = '';
        this.fetchOrders();
      }
    },
    pagination: {
      handler() {
        if (this.value) {
          this.fetchOrders();
        }
      },
      deep: true
    }
  },
  methods: {
    onOrderTypeChange() {
      this.pagination.page = 1;
      this.fetchOrders();
    },
    getStatusColor(val) {
      return this.$options.filters.display_color(val, this.packageStatus);
    },
    fetchOrders() {
      if (!this.value) return;
      this.loading = true;
      const endpoint = this.orderType === 0 ? 'main_order' : 'deposit';

      const filter = [];
      if (this.orderType === 0) {
        filter.push({
          column: 'Status',
          condition: 'in',
          value: [
            configs.ORDER_STATUS_ENUM.da_mua_hang,
            configs.ORDER_STATUS_ENUM.dang_van_chuyen,
            configs.ORDER_STATUS_ENUM.da_nhap_kho_tq,
            configs.ORDER_STATUS_ENUM.van_chuyen_ve_vn,
            configs.ORDER_STATUS_ENUM.da_nhap_kho_vn,
            configs.ORDER_STATUS_ENUM.cho_thanh_toan,
            configs.ORDER_STATUS_ENUM.da_xuat_kho,
            configs.ORDER_STATUS_ENUM.khieu_nai
          ]
        });
      }

      const param = {
        pagination: this.pagination,
        filter: filter,
        quickFilter: this.searchQuery ? [{ column: 'ID', value: this.searchQuery, condition: 'contains' }] : []
      };

      request_list(
        endpoint,
        res => {
          this.loading = false;
          if (res && res.data) {
            this.items = res.data;
            this.totalRows = res.total || 0;
          } else {
            this.items = [];
            this.totalRows = 0;
          }
        },
        Vue.http,
        param,
        this.$store.state.authentication.token
      );
    },
    attachOrder(order) {
      if (!this.transactionItem || !order) return;
      this.submitting = true;
      this.selectedOrderId = order.ID;

      const payload = {
        OrderID: order.ID,
        PackageType: this.orderType,
        UID: order.UID || null,
        Type: 'transaction',
        ModifiedBy: this.$store.state.authentication.user ? this.$store.state.authentication.user.username : ''
      };

      update_detail(
        'order_transactions',
        res => {
          this.submitting = false;
          this.selectedOrderId = null;
          if (res && res.data && res.data.status === 'fail') {
            sendErrorNotice('Không thể gắn đơn cho mã vận đơn.');
          } else {
            sendSuccessNotice(`Đã gắn mã vận đơn ${this.transactionItem.TransactionCode} vào đơn ${this.orderType === 1 ? 'KG' : 'MD'}${order.ID} thành công.`);
            this.$emit('success');
            this.closeDialog();
          }
        },
        Vue.http,
        payload,
        this.transactionItem.ID,
        this.$store.state.authentication.token
      );
    },
    closeDialog() {
      this.dialogModel = false;
    }
  }
};
</script>
